import { Box, Grid } from "@mui/material";
import {
    //@ts-ignore
    StriimBadge,
    StriimButton,
    StriimFeedbackBlock,
    //@ts-ignore
    StriimList,
    //@ts-ignore
    StriimListItem,
    StriimTypography,
    StriimTooltip
} from "@striim/striim-ui";
//@ts-ignore
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import ErrorIcon from "src/generic/icon/wizard-icon/errorFilled.svg";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { FillCheck } from "src/generic/icon/customIcons";
import DeleteIcon from "src/generic/icon/manage-striim/delete.svg";
import { styles } from "./add-queries-modal.styles";
import { QueryItem } from "./add-queries-modal";

const MAX_ALLOWED_QUERIES = 20;

interface QuerySidebarProps {
    sortedQueries: QueryItem[];
    queries: QueryItem[];
    selectedQueryId: string;
    queryErrors: Record<string, string>;
    validatedQueries: Set<string>;
    emptyQueryIds: Set<string>;
    onSelect: (id: string) => void;
    onDragEnd: (result: DropResult) => void;
    onAdd: () => void;
    onRequestDelete: (id: string) => void;
    isCacheComponent: boolean;
}

const QuerySidebar: React.FC<QuerySidebarProps> = ({
    sortedQueries,
    queries,
    selectedQueryId,
    queryErrors,
    validatedQueries,
    emptyQueryIds,
    isCacheComponent,
    onSelect,
    onDragEnd,
    onAdd,
    onRequestDelete
}) => {
    return (
        <Grid item sx={styles.sidebar} flexBasis="30%" flexShrink={0} data-test-id="query-sidebar">
            <Box sx={styles.sidebarScrollable}>
                <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="queries-list">
                        {provided => (
                            <StriimList ref={provided.innerRef} {...provided.droppableProps} sx={styles.queryList}>
                                {sortedQueries.map((query, index) => {
                                    const hasEmptyError = emptyQueryIds.has(query.id);
                                    const hasError = !!queryErrors[query.id] || hasEmptyError;
                                    return (
                                        <Draggable key={query.id} draggableId={query.id} index={index}>
                                            {provided => (
                                                <StriimListItem
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    sx={[
                                                        styles.sidebarItem,
                                                        hasError && styles.sidebarItemError,
                                                        selectedQueryId === query.id &&
                                                            (hasError
                                                                ? styles.sidebarItemErrorSelected
                                                                : styles.sidebarItemSelected)
                                                    ]}
                                                    data-test-id={`query-item-${query.identifier}`}
                                                    onClick={() => onSelect(query.id)}
                                                >
                                                    <Box sx={styles.sidebarItemLeft}>
                                                        <Box
                                                            {...provided.dragHandleProps}
                                                            data-test-id={`drag-handle-${query.identifier}`}
                                                            sx={{ display: "flex", alignItems: "center" }}
                                                        >
                                                            <DragIndicatorIcon sx={styles.dragIcon} />
                                                        </Box>
                                                        <StriimBadge
                                                            badgeContent={String(index + 1).padStart(2, "0")}
                                                            inline
                                                        />
                                                        <StriimTooltip title={query.identifier}>
                                                            <StriimTypography
                                                                variant="body4"
                                                                color="greyscale.900"
                                                                sx={styles.identifierLabel}
                                                                data-test-id={`query-name-${query.identifier}`}
                                                            >
                                                                {query.identifier}
                                                            </StriimTypography>
                                                        </StriimTooltip>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 1
                                                        }}
                                                    >
                                                        {hasError && (
                                                            <ErrorIcon
                                                                sx={styles.sidebarErrorIcon}
                                                                data-test-id={`query-${query.identifier}-error-icon`}
                                                            />
                                                        )}
                                                        {validatedQueries.has(query.id) && (
                                                            <FillCheck
                                                                sx={styles.sidebarSuccessIcon}
                                                                data-test-id={`query-${query.identifier}-validated-icon`}
                                                            />
                                                        )}
                                                        <Box
                                                            component="span"
                                                            sx={styles.deleteIcon}
                                                            onClick={e => {
                                                                e.stopPropagation();
                                                                onRequestDelete(query.id);
                                                            }}
                                                            data-test-id={`delete-query-${query.identifier}`}
                                                        >
                                                            <DeleteIcon />
                                                        </Box>
                                                    </Box>
                                                </StriimListItem>
                                            )}
                                        </Draggable>
                                    );
                                })}
                                {provided.placeholder}
                            </StriimList>
                        )}
                    </Droppable>
                </DragDropContext>
                {isCacheComponent && (
                    <Box sx={{ px: 2, pt: 1 }}>
                        <StriimFeedbackBlock
                            type="small"
                            variant="info"
                            content={
                                <StriimTypography
                                    variant="body4"
                                    color="greyscale.900"
                                    data-test-id="cache-component-info"
                                >
                                    Only one query is supported in the DB Cache component.
                                </StriimTypography>
                            }
                        />
                    </Box>
                )}
            </Box>
            {!isCacheComponent && (
                <Box sx={styles.addQueryButton}>
                    {queries.length >= MAX_ALLOWED_QUERIES ? (
                        <StriimFeedbackBlock
                            type="small"
                            variant="info"
                            content={
                                <StriimTypography variant="body4" color="greyscale.900">
                                    You've reached the limit of {MAX_ALLOWED_QUERIES} queries.
                                </StriimTypography>
                            }
                        />
                    ) : (
                        <StriimButton variant="text" onClick={onAdd} data-testid="add-query-button">
                            + Add Query
                        </StriimButton>
                    )}
                </Box>
            )}
        </Grid>
    );
};

export default QuerySidebar;
